import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchTemplates, previewDocument, generateAndSaveDocument, downloadGeneratedDocument, generateDocumentOnly
} from './api';
import { DocumentTemplateRead, PreviewResponse } from './types';
import {
  ArrowBackOutlined as BackIcon,
  DescriptionOutlined as DocIcon,
  WarningAmberOutlined as WarningIcon,
  DownloadOutlined as DownloadIcon
} from '@mui/icons-material';

const GenerateDocument: React.FC = () => {
  const { applicationId } = useParams<{ applicationId: string }>();
  const navigate = useNavigate();
  const appId = Number(applicationId);

  const [templates, setTemplates] = useState<DocumentTemplateRead[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');

  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [manualValues, setManualValues] = useState<Record<string, any>>({});

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generatedDocId, setGeneratedDocId] = useState<number | null>(null);
  const [generatedFileName, setGeneratedFileName] = useState<string>('');

  useEffect(() => {
    fetchTemplates(undefined, true).then(setTemplates).catch(console.error);
  }, []);

  const currentTemplate = templates.find(t => String(t.id) === selectedTemplateId);

  const manualFieldsToRender = currentTemplate?.manual_fields
    ? Object.entries(currentTemplate.manual_fields).map(([key, field]: [string, any]) => ({
        key,
        label: field.label,
        type: field.type,
        required: field.required,
        placeholder: field.placeholder
      }))
    : [];

  const handleTemplateSelect = async (templateId: string) => {
    setSelectedTemplateId(templateId);
    setGeneratedDocId(null);
    setManualValues({});
    if (!templateId) {
      setPreview(null);
      return;
    }

    setLoading(true);
    try {
      const data = await previewDocument({
        template_id: Number(templateId),
        application_id: appId,
        manual_values: {}
      });
      setPreview(data);
    } catch (error: any) {
      alert(`Ошибка предпросмотра: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleManualValueChange = (key: string, value: any) => {
    setManualValues(prev => ({ ...prev, [key]: value }));
  };

  const handleTestGenerate = async () => {
    if (!selectedTemplateId) return;

    setTesting(true);
    try {
      const templateName = templates.find(t => String(t.id) === selectedTemplateId)?.name || 'document';
      await generateDocumentOnly({
        template_id: Number(selectedTemplateId),
        application_id: appId,
        manual_values: manualValues
      }, templateName);
    } catch (error: any) {
      alert(`Ошибка при генерации документа: ${error.message}`);
    } finally {
      setTesting(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedTemplateId) return;

    const missingRequired = manualFieldsToRender.filter(
      f => f.required && !manualValues[f.key]
    );

    if (missingRequired.length > 0) {
      alert('Пожалуйста, заполните все обязательные поля.');
      return;
    }

    setGenerating(true);
    try {
      const doc = await generateAndSaveDocument({
        template_id: Number(selectedTemplateId),
        application_id: appId,
        manual_values: manualValues
      });

      setGeneratedDocId(doc.id);
      setGeneratedFileName(doc.file_name);

      await downloadGeneratedDocument(doc.id, doc.file_name);

    } catch (error: any) {
      alert(`Ошибка при генерации документа: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">

        {/* Контейнер карточки (ограничен по ширине, чтобы форма не была слишком растянутой) */}
        <div className="max-w-4xl space-y-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors font-medium"
          >
            <BackIcon fontSize="small" /> Назад к заявке
          </button>

          <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <DocIcon className="text-[#1E61AF]" />
              Генерация документа (Заявка #{appId})
            </h1>

            {/* ШАГ 1: ВЫБОР ШАБЛОНА */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Выберите шаблон документа <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
              >
                <option value="">-- Выберите шаблон --</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {loading && <div className="text-blue-600 font-medium animate-pulse">Анализ шаблона и данных БД...</div>}

            {/* ШАГ 2: ПРЕДПРОСМОТР И РУЧНОЙ ВВОД */}
            {preview && !loading && !generatedDocId && (
              <div className="space-y-8 animate-fade-in">

                {preview.missing_in_db.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex gap-3">
                    <WarningIcon className="text-yellow-600 shrink-0" />
                    <div>
                      <h4 className="text-sm font-semibold text-yellow-800">Внимание: В БД отсутствуют данные для некоторых полей</h4>
                      <p className="text-xs text-yellow-700 mt-1">Эти поля в документе останутся пустыми:</p>
                      <ul className="list-disc list-inside text-xs text-yellow-700 mt-1 ml-4 font-mono">
                        {preview.missing_in_db.map((path, idx) => <li key={path || idx}>{path}</li>)}
                      </ul>
                    </div>
                  </div>
                )}

                {preview.unmapped_tags.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-red-800">В шаблоне есть ненастроенные теги:</h4>
                    <p className="text-xs text-red-700 mt-1 font-mono">{preview.unmapped_tags.join(', ')}</p>
                  </div>
                )}

                {manualFieldsToRender.length > 0 && (
                  <div className="border-t border-gray-200 pt-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Данные для ручного ввода</h3>
                    <div className="space-y-4">
                      {manualFieldsToRender.map(field => (
                        <div key={field.key}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {field.label || field.key} {field.required && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                            required={field.required}
                            value={manualValues[field.key] || ''}
                            onChange={(e) => handleManualValueChange(field.key, e.target.value)}
                            placeholder={field.placeholder || ''}
                            className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Кнопки генерации */}
                <div className="pt-6 flex flex-col sm:flex-row justify-end gap-4">
                  <button
                    onClick={handleTestGenerate}
                    disabled={generating || testing}
                    className="border border-[#1E61AF] text-[#1E61AF] px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {testing ? 'Загрузка...' : 'Скачать без сохранения'}
                  </button>

                  <button
                    onClick={handleGenerate}
                    disabled={generating || testing}
                    className="bg-[#1E61AF] text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-800 transition-colors disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {generating ? 'Формирование документа...' : 'Сформировать и сохранить'}
                  </button>
                </div>
              </div>
            )}

            {/* ШАГ 3: УСПЕХ */}
            {generatedDocId && (
              <div className="mt-8 bg-green-50 border border-green-200 rounded-xl p-8 text-center animate-fade-in">
                <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DocIcon fontSize="large" />
                </div>
                <h2 className="text-2xl font-bold text-green-800 mb-2">Документ успешно сформирован!</h2>
                <p className="text-green-700 mb-6 text-sm">Документ сохранен в системе и загружен на ваше устройство.</p>

                <button
                  onClick={() => downloadGeneratedDocument(generatedDocId, generatedFileName)}
                  className="bg-white border border-green-300 text-green-700 px-6 py-2.5 rounded-lg font-medium hover:bg-green-100 transition-colors flex items-center gap-2 mx-auto"
                >
                  <DownloadIcon fontSize="small" /> Скачать еще раз
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateDocument;